import React, { useState, useEffect, useCallback, FC, useRef } from 'react';
import { Modal } from '@consta/uikit/Modal';
import { Button } from '@consta/uikit/Button';
import { Text } from '@consta/uikit/Text';
import { Combobox } from '@consta/uikit/Combobox';
import { IconClose } from '@consta/icons/IconClose';
import { IconUpload } from '@consta/icons/IconUpload';
import { IconTrash } from '@consta/icons/IconTrash';
import { useLazyGetAreaQuery } from '../../../services/areaService';
import { useWellImportMutation } from '../../../services/wellService';

interface LoadDataModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectUuid?: string;
    onSuccess?: () => void;
}

const LoadDataModal: FC<LoadDataModalProps> = ({ 
    isOpen, 
    onClose, 
    projectUuid,
    onSuccess 
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null | any>(null);
    const [selectedArea, setSelectedArea] = useState<any>(null);
    const [areaItems, setAreaItems] = useState<any[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState<any>()
    //(selectedArea)

    // Хук для получения участков
    const [getAreas, { data: areasData, isLoading: isLoadingAreas }] = useLazyGetAreaQuery();
    
    // Хук для импорта скважин
    const [wellImport, { isLoading: isImporting }] = useWellImportMutation();

    // Загрузка участков при открытии модалки
    useEffect(() => {
        if (isOpen && projectUuid) {
            loadAreas();
        }
    }, [isOpen, projectUuid]);

    const loadAreas = async () => {
        if (!projectUuid) return;
        
        try {
            await getAreas({
                limit: 100,
                offset: 0,
                ordering: 'name',
                project: [projectUuid],
                // unassigned: true,
            }).unwrap();
        } catch (error) {
            console.error('❌ Ошибка загрузки участков:', error);
        }
    };

    // Обработка данных участков
    useEffect(() => {
        if (areasData?.results) {
            const areas = areasData.results.map((area: any) => ({
                label: area.name || 'Неизвестный участок',
                id: area.uuid,
                ...area
            }));
            setAreaItems(areas);
        }
    }, [areasData]);

    // Валидация типа файла
    const isValidFileType = (file: File): boolean => {
        const allowedExtensions = ['.xlsx', '.xls', '.csv'];
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        return !!(fileExtension && allowedExtensions.includes(fileExtension));
    };

    // Обработчик drag&drop событий
    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (isValidFileType(file)) {
                setSelectedFile(file);
            } else {
                
            }
        }
    }, []);

    // Обработчик выбора файла через input
    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (isValidFileType(file)) {
                setSelectedFile(file);
            } else {
               
            }
        }
    };

    // Обработчик клика по области загрузки
    const handleUploadAreaClick = () => {
        fileInputRef.current?.click();
    };

    // Обработчик удаления файла
    const handleRemoveFile = () => {
        setSelectedFile(null);
        setUploadProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Обработчик отправки формы
    const handleSubmit = async () => {
        

        try {
            setUploadProgress(10);
            
            // Имитация прогресса загрузки
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return prev;
                    }
                    return prev + 10;
                });
            }, 200);

            // Вызов мутации импорта
            await wellImport({
                file: selectedFile,
                area_uuid: selectedArea.uuid
            }).unwrap();
            

            clearInterval(progressInterval);
            setUploadProgress(100);

            // Успешная загрузка
            setTimeout(() => {
                handleClose();
                if (onSuccess) {
                    onSuccess();
                }
            }, 500);

        } catch (error: any) {
            console.error('❌ Ошибка при загрузке файла:', error);
            
            // Более информативное сообщение об ошибке
            const errorMessage = error?.data?.message || 
                               error?.data?.detail || 
                               'Произошла ошибка при загрузке файла. Пожалуйста, попробуйте еще раз.';
            
            setUploadProgress(0);
            setError(error?.data?.message)
        }
    };

    // Обработчик закрытия модалки
    const handleClose = () => {
        setSelectedFile(null);
        setSelectedArea(null);
        setUploadProgress(0);
        setIsDragOver(false);
        setError('')
        onClose();
    };

    const isSubmitDisabled = !selectedFile || !selectedArea || isImporting;

    return (
        <Modal
            isOpen={isOpen}
            hasOverlay
            onClickOutside={handleClose}
            onEsc={handleClose}
            className="load-data-modal"
        >
            <div style={{ 
                padding: '20px', 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '20px',
                minWidth: '500px',
                maxWidth: '600px'
            }}>
                {/* Заголовок */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                }}>
                    <Text size='xl' weight='bold'>Загрузить данные скважин</Text>
                    <Button 
                        size='s' 
                        iconLeft={IconClose} 
                        view='clear' 
                        onClick={handleClose}
                        disabled={isImporting}
                    />
                </div>

                {/* Комбобокс выбора участка */}
                <div>
                    {/* <Text size='s' view='secondary' style={{ marginBottom: '8px' }}>
                        Участок 
                    </Text> */}
                    <Combobox
                        label='Участок'
                        required
                        items={areaItems}
                        value={selectedArea}
                        onChange={setSelectedArea}
                        placeholder={isLoadingAreas ? "Загрузка участков..." : "Выберите участок"}
                        disabled={isLoadingAreas || isImporting}
                    />
                </div>

                {/* Область drag&drop */}
                <div>
                    <Text size='s' view='secondary' style={{ marginBottom: '8px' }} required>
                        Файл с данными скважин *
                    </Text>
                    <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={handleUploadAreaClick}
                        style={{
                            border: `2px dashed ${isDragOver ? '#0070f0' : '#e0e0e0'}`,
                            borderRadius: '8px',
                            padding: '32px 20px',
                            textAlign: 'center',
                            backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
                            cursor: isImporting ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            opacity: isImporting ? 0.6 : 1,
                        }}
                    >
                        {selectedFile ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <Text weight="semibold" style={{ wordBreak: 'break-word' }}>
                                        {selectedFile.name}
                                    </Text>
                                    <Text size="s" view="secondary">
                                        Размер: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                    </Text>
                                </div>
                                {!isImporting && (
                                    <Button
                                        size="s"
                                        view="clear"
                                        iconLeft={IconTrash}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveFile();
                                        }}
                                        style={{ color: '#ff4d4f' }}
                                        label='Удалить'
                                    />
                                )}
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                {/*@ts-ignore */}
                                <IconUpload size="m" view="secondary" />
                                <div style={{ textAlign: 'center' }}>
                                    <Text weight="semibold">Перетащите файл сюда</Text>
                                    <Text size="s" view="secondary">или нажмите для выбора</Text>
                                </div>
                                <Text size="s" view="secondary">
                                    Поддерживаемые форматы: .xlsx, .xls, .csv
                                </Text>
                            </div>
                        )}
                    </div>

                    {/* Скрытый input для файлов */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        style={{ display: 'none' }}
                        onChange={handleFileInputChange}
                        accept=".xlsx,.xls,.csv"
                        disabled={isImporting}
                    />
                </div>

                {/* Прогресс бар */}
                {isImporting && uploadProgress > 0 && (
                    <div>
                        <Text size="s" view="secondary" style={{ marginBottom: '8px' }}>
                            Загрузка: {uploadProgress}%
                        </Text>
                        <div
                            style={{
                                width: '100%',
                                height: '6px',
                                backgroundColor: '#e0e0e0',
                                borderRadius: '3px',
                                overflow: 'hidden'
                            }}
                        >
                            <div
                                style={{
                                    width: `${uploadProgress}%`,
                                    height: '100%',
                                    backgroundColor: '#0070f0',
                                    transition: 'width 0.3s ease',
                                    borderRadius: '3px'
                                }}
                            />
                        </div>
                    </div>
                )}

                <Text size='xs' style={{color: 'red'}}>{error}</Text>

                {/* Кнопки действий */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: '12px',
                    marginTop: '16px'
                }}>
                    <Button
                        label="Отмена"
                        view="ghost"
                        onClick={handleClose}
                        disabled={isImporting}
                    />
                    <Button
                        label={isImporting ? "Загрузка..." : "Загрузить"}
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                        loading={isImporting}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default LoadDataModal;